import { inject, Injectable } from '@angular/core';

import {
  ComparisonOperator,
  JoinCondition,
  LogicalType,
  QueryNodeType,
  WorkflowId,
  WorkflowNodeId,
} from '@blaze-shared/api-contracts-domain';
import { CanvasElementInputBinding } from '@blaze-shared/elements-domain';
import { deepClone } from '@blaze-shared/shared-util';
import {
  AggregateWorkflowNode,
  Connection,
  findFirstNodeInWorkflowOfType,
  GetWorkflowNode,
  isGetNode,
  JoinWorkflowNode,
  Workflow,
  WorkflowNode,
} from '@blaze-shared/workflow-domain';

import { RuntimeFacade } from '../+state';
import { ChatElement } from '../elements';
import { ChatSchemaService } from '../elements/chat/chat/chat-schema.service';
import { createConnection, getUiRuntimeElement, getWorkflowInputName } from '../utils/runtime';

const MESSAGES_SELECT_NODE_ID = 'messages_data-source-node';
const CONVERSATIONS_INFO_SELECT_NODE_ID = 'conversations-info_data-source-node';
const MESSAGES_CONVERSATIONS_INFO_JOIN_NODE_ID = 'messages-conversations-info_join';
const AGGREGATE_MESSAGES_NODE_ID = 'messages_aggregate';

const UNREAD_MESSAGES_AGGREGATE_ALIAS = 'unreadMessages';

export const ALLOWED_COMPARISON_OPERATORS_MAP: Record<ComparisonOperator, string> = {
  [ComparisonOperator.Eq]: '=',
  [ComparisonOperator.Gt]: '>',
  [ComparisonOperator.Gte]: '>=',
  [ComparisonOperator.Lt]: '<',
  [ComparisonOperator.Lte]: '<=',
} as Record<ComparisonOperator, string>;

@Injectable({
  providedIn: 'root',
})
export class ChatWorkflowGenerationService {
  private readonly chatSchemaService = inject(ChatSchemaService);
  private readonly runtimeFacade = inject(RuntimeFacade);

  ensureConversationSelectConnection(
    element: ChatElement,
  ): { connection: Connection; shouldAdd: boolean } | null {
    const workflow = this.findWorkflow(element, 'messages');

    if (!workflow) {
      return null;
    }

    const connection: Connection | undefined = this.runtimeFacade.connections.find(
      (c) =>
        c.source.id === element.id &&
        c.eventType === 'chat-conversation-select' &&
        c.target.id === workflow.id,
    );

    if (connection) {
      return { connection, shouldAdd: false };
    }

    return {
      connection: this.createChatConversationSelectConnection(element, workflow.id),
      shouldAdd: true,
    };
  }

  prepareMessagesWorkflow(element: ChatElement, connection: Connection): Workflow | null {
    const workflow = this.findWorkflow(element, 'messages');

    if (!workflow) {
      return null;
    }

    const firstGetNode = findFirstNodeInWorkflowOfType(workflow, isGetNode);

    if (firstGetNode?.id === MESSAGES_SELECT_NODE_ID) {
      return null;
    }

    const shouldReplaceCurrentNodes = !firstGetNode?.nextNodeId;
    const inputName = getWorkflowInputName(element);

    if (!workflow.inputs[inputName]) {
      workflow.inputs[inputName] = connection.id;
    }

    const nodes: WorkflowNode[] = [
      this.getMessagesFirstSelectDatasetNode(element, inputName),
      this.getMessagesJoinNode(
        element,
        shouldReplaceCurrentNodes ? null : (workflow.startNodeId ?? null),
      ),
      this.getMessagesSecondSelectDatasetNode(element, inputName),
    ];

    if (!shouldReplaceCurrentNodes) {
      nodes.push(...workflow.nodes);
    }

    workflow.isDraft = true;
    workflow.startNodeId = nodes[0].id;
    workflow.nodes = nodes;

    return workflow;
  }

  prepareConversationsInfoWorkflow(element: ChatElement): Workflow | null {
    const workflow = this.findWorkflow(element, 'conversationsInfo');

    if (!workflow) {
      return null;
    }

    const firstGetNode = findFirstNodeInWorkflowOfType(workflow, isGetNode);

    if (firstGetNode?.id === CONVERSATIONS_INFO_SELECT_NODE_ID) {
      return null;
    }

    const shouldReplaceCurrentNodes = !firstGetNode?.nextNodeId;

    const nodes: WorkflowNode[] = [
      this.getConversationsInfoFirstSelectDatasetNode(element),
      this.getConversationsInfoJoinNode(element),
      this.getConversationsInfoSecondSelectDatasetNode(element),
      this.getConversationsInfoAggregateNode(
        element,
        shouldReplaceCurrentNodes ? null : (workflow.startNodeId ?? null),
      ),
    ];

    if (!shouldReplaceCurrentNodes) {
      nodes.push(...workflow.nodes);
    }

    workflow.isDraft = true;
    workflow.startNodeId = nodes[0].id;
    workflow.nodes = nodes;

    return workflow;
  }

  private getConversationsInfoAggregateNode(
    element: ChatElement,
    nextNodeId: WorkflowNodeId | null,
  ): AggregateWorkflowNode {
    const conversationsInfoConversationColumn =
      element.props.content.input?.conversationsInfo.conversation;
    const conversationsInfoLastMessageIdColumn =
      element.props.content.input?.conversationsInfo.lastMessageId;

    if (!conversationsInfoConversationColumn) {
      throw new Error('Conversation column not defined');
    }

    if (!conversationsInfoLastMessageIdColumn) {
      throw new Error('Last Message Id column not defined');
    }

    return {
      id: AGGREGATE_MESSAGES_NODE_ID,
      nodeType: 'Aggregate',
      functionDefs: [
        {
          name: 'COUNT',
          column: '',
          alias: UNREAD_MESSAGES_AGGREGATE_ALIAS,
        },
      ],
      groupByColumns: [conversationsInfoConversationColumn, conversationsInfoLastMessageIdColumn],
      nextNodeId,
    };
  }

  private getConversationsInfoSecondSelectDatasetNode(element: ChatElement): GetWorkflowNode {
    return {
      datasetId: this.chatSchemaService.getBlazeTableId(element, 'messages'),
      id: MESSAGES_SELECT_NODE_ID,
      ignoreExternalAccountFiltering: false,
      isFormulaCondition: false,
      nextNodeId: null,
      nodeType: 'Get',
      projectColumns: [],
      query: undefined,
      queryConditions: [],
    };
  }

  private getConversationsInfoFirstSelectDatasetNode(element: ChatElement): GetWorkflowNode {
    const conversationsInfoEmailColumn =
      element.props.content.input?.conversationsInfo.membersEmail;

    if (!conversationsInfoEmailColumn) {
      throw new Error('Email column not defined');
    }

    return {
      id: CONVERSATIONS_INFO_SELECT_NODE_ID,
      nodeType: 'Get',
      datasetId: this.chatSchemaService.getBlazeTableId(element, 'conversationsInfo'),
      projectColumns: [],
      query: {
        nodeType: QueryNodeType.Logical,
        logicalType: LogicalType.And,
        children: [
          {
            nodeType: QueryNodeType.Comparison,
            column: conversationsInfoEmailColumn,
            operator: ComparisonOperator.Eq,
            value: `$$User["Email"]`,
            displayInfos: [
              {
                display: 'Current User | Email',
                formulaName: '',
                isAutoCompleted: true,
                value: '$$User["Email"]',
              },
            ],
            conditionId: '',
            isOptional: false,
            isFormulaValue: false,
          },
        ],
      },
      ignoreExternalAccountFiltering: false,
      isFormulaCondition: false,
      nextNodeId: MESSAGES_CONVERSATIONS_INFO_JOIN_NODE_ID,
      queryConditions: [],
    };
  }

  private getMessagesFirstSelectDatasetNode(
    element: ChatElement,
    inputName: string,
  ): GetWorkflowNode {
    const messagesConversationColumn = element.props.content.input?.messages.conversation;
    if (!messagesConversationColumn) {
      throw new Error('Conversation column not defined');
    }

    const conversationSchemaId = this.chatSchemaService.getBlazeTableId(element, 'conversations');

    if (!conversationSchemaId) {
      throw new Error('Conversation Schema not found');
    }

    const conversationsRowIdColumn = this.runtimeFacade
      .getSchemaById(conversationSchemaId)
      .columns.find((column) => column.isId)?.name;

    return {
      id: MESSAGES_SELECT_NODE_ID,
      nodeType: 'Get',
      datasetId: this.chatSchemaService.getBlazeTableId(element, 'messages'),
      projectColumns: [],
      query: {
        nodeType: QueryNodeType.Logical,
        logicalType: LogicalType.And,
        children: [
          {
            nodeType: QueryNodeType.Comparison,
            column: messagesConversationColumn,
            operator: ComparisonOperator.Eq,
            value: `$$${inputName}["${conversationsRowIdColumn}"]`,
            displayInfos: [],
            conditionId: '',
            isOptional: false,
            isFormulaValue: false,
          },
        ],
      },
      ignoreExternalAccountFiltering: false,
      isFormulaCondition: false,
      queryConditions: [],
      nextNodeId: MESSAGES_CONVERSATIONS_INFO_JOIN_NODE_ID,
    };
  }

  private getMessagesSecondSelectDatasetNode(
    element: ChatElement,
    inputName: string,
  ): GetWorkflowNode {
    const conversationsInfoConversationColumn =
      element.props.content.input?.conversationsInfo.conversation;
    const conversationsInfoEmailColumn =
      element.props.content.input?.conversationsInfo.membersEmail;
    const conversationSchemaId = this.chatSchemaService.getBlazeTableId(element, 'conversations');

    if (!conversationSchemaId) {
      throw new Error('Conversation Schema not found');
    }

    const conversationsRowIdColumn = this.runtimeFacade
      .getSchemaById(conversationSchemaId)
      .columns.find((column) => column.isId)?.name;

    if (!conversationsInfoEmailColumn) {
      throw new Error('Email column not defined');
    }

    if (!conversationsInfoConversationColumn) {
      throw new Error('Conversation column not defined');
    }

    return {
      id: CONVERSATIONS_INFO_SELECT_NODE_ID,
      nodeType: 'Get',
      datasetId: this.chatSchemaService.getBlazeTableId(element, 'conversationsInfo'),
      projectColumns: [],
      query: {
        nodeType: QueryNodeType.Logical,
        logicalType: LogicalType.And,
        children: [
          {
            nodeType: QueryNodeType.Comparison,
            column: conversationsInfoConversationColumn,
            operator: ComparisonOperator.Eq,
            value: `$$${inputName}["${conversationsRowIdColumn}"]`,
            displayInfos: [
              {
                value: `$$${inputName}["${conversationsRowIdColumn}"]`,
                display: `${inputName} | ${conversationsRowIdColumn}`,
                isAutoCompleted: true,
                formulaName: '',
              },
            ],
            conditionId: '',
            isOptional: false,
            isFormulaValue: false,
          },
          {
            nodeType: QueryNodeType.Comparison,
            column: conversationsInfoEmailColumn,
            operator: ComparisonOperator.Eq,
            value: `$$User["Email"]`,
            displayInfos: [
              {
                display: 'Current User | Email',
                formulaName: '',
                isAutoCompleted: true,
                value: '$$User["Email"]',
              },
            ],
            conditionId: '',
            isOptional: false,
            isFormulaValue: false,
          },
        ],
      },
      isFormulaCondition: false,
      ignoreExternalAccountFiltering: false,
      queryConditions: [],
      nextNodeId: null,
    };
  }

  private getMessagesJoinNode(
    element: ChatElement,
    nextNodeId: WorkflowNodeId | null,
  ): JoinWorkflowNode {
    const messagesConversationColumn = element.props.content.input?.messages.conversation;
    const conversationsInfoConversationColumn =
      element.props.content.input?.conversationsInfo.conversation;

    if (!messagesConversationColumn || !conversationsInfoConversationColumn) {
      throw new Error('Conversation column not defined');
    }

    const node: JoinWorkflowNode = {
      id: MESSAGES_CONVERSATIONS_INFO_JOIN_NODE_ID,
      nodeType: 'Join',
      joinType: 'LeftOuter',
      targetId: CONVERSATIONS_INFO_SELECT_NODE_ID,
      isFormulaCondition: true,
      joinOperator: LogicalType.And,
      conditionList: [
        {
          column: messagesConversationColumn,
          value: conversationsInfoConversationColumn,
          operator: ComparisonOperator.Eq,
          displayInfos: [],
          isFormulaValue: false,
        },
      ],
      nextNodeId,
    };

    node.condition = this.updateConditionFormula(node.conditionList, node.joinOperator);

    return node;
  }

  private getConversationsInfoJoinNode(element: ChatElement): JoinWorkflowNode {
    const messagesConversationColumn = element.props.content.input?.messages.conversation;
    const conversationsInfoConversationColumn =
      element.props.content.input?.conversationsInfo.conversation;
    const lastMessageIdColumn = element.props.content.input?.conversationsInfo.lastMessageId;
    const messagesIdColumn = element.props.content.input?.messages.id;

    if (!messagesConversationColumn || !conversationsInfoConversationColumn) {
      throw new Error('Conversation column not defined');
    }

    if (!lastMessageIdColumn) {
      throw new Error('Last message ID column not defined');
    }

    if (!messagesIdColumn) {
      throw new Error('ID column not defined');
    }

    const node: JoinWorkflowNode = {
      id: MESSAGES_CONVERSATIONS_INFO_JOIN_NODE_ID,
      nodeType: 'Join',
      joinType: 'Inner',
      targetId: MESSAGES_SELECT_NODE_ID,
      isFormulaCondition: true,
      joinOperator: LogicalType.And,
      conditionList: [
        {
          column: conversationsInfoConversationColumn,
          value: messagesConversationColumn,
          operator: ComparisonOperator.Eq,
          displayInfos: [],
          isFormulaValue: false,
        },
        {
          column: lastMessageIdColumn,
          value: messagesIdColumn,
          operator: ComparisonOperator.Lt,
          displayInfos: [],
          isFormulaValue: false,
        },
      ],
      nextNodeId: AGGREGATE_MESSAGES_NODE_ID,
    };

    node.condition = this.updateConditionFormula(node.conditionList, node.joinOperator);

    return node;
  }

  private createChatConversationSelectConnection(
    element: ChatElement,
    workflowId: WorkflowId,
  ): Connection {
    return createConnection(
      element.ns,
      {
        id: element.id,
        layerType: 'Ui',
      },
      {
        id: workflowId,
        layerType: 'Workflow',
      },
      'chat-conversation-select',
    );
  }

  private findWorkflow(
    element: ChatElement,
    dataSourceKey: CanvasElementInputBinding,
  ): Workflow | null {
    const dataSourceId = element.props.data?.find((data) => data.key === dataSourceKey)?.dataSource
      ?.id;

    if (!dataSourceId) {
      return null;
    }

    return (
      deepClone(
        this.runtimeFacade
          .getWorkflowsByTarget(getUiRuntimeElement(element.id))
          .find((workflow) => workflow.id === dataSourceId),
      ) ?? null
    );
  }

  private updateConditionFormula(conditions: JoinCondition[], joinOperator: LogicalType): string {
    const joinConditionFormulaList = [];
    let condition = '';
    for (const condition of conditions) {
      const operator = condition.operator
        ? ALLOWED_COMPARISON_OPERATORS_MAP[condition.operator]
        : '';
      const input1 = condition?.column || '';
      const input2 = condition.value ?? '';
      if (input1 && input2 && operator) {
        joinConditionFormulaList.push(`$$input1["${input1}"] ${operator} $$input2["${input2}"]`);
      }
    }
    const joinFormula = joinOperator === LogicalType.And ? 'AND' : 'OR';
    if (joinConditionFormulaList.length === 1) {
      condition = joinConditionFormulaList[0];
    } else {
      const joinConditions = joinConditionFormulaList.join(', ');
      condition = `${joinFormula}(${joinConditions})`;
    }

    return condition;
  }
}
